import {
  BoardEvent,
  BoardEventType,
  BoardType,
  ReadonlyToken,
} from "@sportactics/board-editing";
import { useEffect, useEffectEvent } from "react";

import {
  AnalyticsEvent,
  AnalyticsEventToken,
  AnalyticsEventType,
  convertTokenToAnalyticsEvent,
  useAnalyticsEvent,
} from "../analytics-event";
import { useBoardDomainEvents } from "../board-domain-events";
import { useBoardState } from "../board-state";

export function useBoardEventAnalyticsBridgeEffect(): void {
  const { subscribe } = useBoardDomainEvents();
  const { board } = useBoardState();
  const { submitEvent } = useAnalyticsEvent();

  const onBoardEvents = useEffectEvent(
    async (
      boardType: BoardType,
      events: readonly BoardEvent[],
    ): Promise<void> => {
      events.forEach((event) => {
        const analyticsEvent = toAnalyticsEvent(
          event,
          boardType,
          board?.tokens,
        );
        if (analyticsEvent !== undefined) submitEvent(analyticsEvent);
      });
    },
  );

  useEffect(() => subscribe(onBoardEvents), [subscribe]);
}

// eslint-disable-next-line max-lines-per-function
function toAnalyticsEvent(
  event: BoardEvent,
  boardType: BoardType,
  tokens: readonly ReadonlyToken[] | undefined,
): AnalyticsEvent | undefined {
  switch (event.type) {
    case BoardEventType.TokenAdded: {
      const token = findToken(tokens, event.id);
      if (token === undefined) return undefined;
      return { type: AnalyticsEventType.AddToken, boardType, token };
    }
    case BoardEventType.TokenMoved: {
      const token = findToken(tokens, event.id);
      if (token === undefined) return undefined;
      return { type: AnalyticsEventType.MoveToken, boardType, token };
    }
    case BoardEventType.TokenRemoved:
      return { type: AnalyticsEventType.RemoveToken, boardType };
    case BoardEventType.DrawingDrawn:
      return {
        type: AnalyticsEventType.DrawDrawing,
        boardType,
        color: event.color,
      };
    case BoardEventType.DrawingErased:
      return { type: AnalyticsEventType.EraseDrawing, boardType };
    case BoardEventType.AllDrawingsErased:
      return { type: AnalyticsEventType.EraseAllDrawings, boardType };
    case BoardEventType.LineDrawn:
      return {
        type: AnalyticsEventType.DrawLine,
        boardType,
        color: event.color,
      };
    case BoardEventType.LineErased:
      return { type: AnalyticsEventType.EraseLine, boardType };
    case BoardEventType.AllLinesErased:
      return { type: AnalyticsEventType.EraseAllLines, boardType };
    case BoardEventType.TextWritten:
      return {
        type: AnalyticsEventType.WriteText,
        boardType,
        color: event.color,
      };
    case BoardEventType.TextEdited:
      return { type: AnalyticsEventType.EditText, boardType };
    case BoardEventType.TextColorChanged:
      return {
        type: AnalyticsEventType.ChangeTextColor,
        boardType,
        color: event.color,
      };
    case BoardEventType.TextSizeChanged:
      return { type: AnalyticsEventType.ChangeTextSize, boardType };
    case BoardEventType.TextMoved:
      return { type: AnalyticsEventType.MoveText, boardType };
    case BoardEventType.TextRemoved:
      return { type: AnalyticsEventType.RemoveText, boardType };
    case BoardEventType.AllTextsRemoved:
      return { type: AnalyticsEventType.RemoveAllTexts, boardType };
    case BoardEventType.BoardReset:
      return { type: AnalyticsEventType.ResetBoard, boardType };
  }
}

function findToken(
  tokens: readonly ReadonlyToken[] | undefined,
  id: string,
): AnalyticsEventToken | undefined {
  const token = tokens?.find((token) => token.id === id);
  return token === undefined ? undefined : convertTokenToAnalyticsEvent(token);
}
